import DateFnsUtils from '@date-io/date-fns';
import {
  Avatar,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemAvatar,
  ListItemSecondaryAction,
  ListItemText,
  Paper,
  Select,
  TextField
} from '@material-ui/core';
import {blue} from '@material-ui/core/colors';
import {makeStyles} from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import CloseIcon from '@material-ui/icons/Close';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import PersonIcon from '@material-ui/icons/Person';
import {
  KeyboardDatePicker,
  MuiPickersUtilsProvider
} from '@material-ui/pickers';
import moment from 'moment';
import PropTypes from 'prop-types';
import React from 'react';
import locale from '../locale/Localization';
import {REGULAR_EXPRESSION_EMAIL} from '../utils/constants';

const useStyles = makeStyles(() => ({
  paper: {
    maxHeight: 300,
    maxWidth: 360,
    overflow: 'auto',
    backgroundColor: '#fafafa'
  },
  listHead: {height: 50, width: 360},
  listBody: {height: 230, width: 360},
  listItem: {backgroundColor: '#fafafa'},
  avatar: {backgroundColor: blue[100], color: blue[800]},
  formControlStyle: {minWidth: 120},
  inputLabelStyle: {width: 375},
  selectStyle: {width: 375, paddingTop: 15},
  closeIcon: {
    cursor: 'pointer',
    float: 'right',
    marginTop: -5,
    marginRight: -10
  }
}));

/**
 * Document parties list with dialog form to add new parties.
 *
 * @param {*} props - Metadata handler function passed as property.
 * @return {String} - Returns the rendered document parties list.
 */
// eslint-disable-next-line max-lines-per-function, require-jsdoc
export default function DocumentParties(props) {
  const classes = useStyles();

  const [open, setOpen] = React.useState(false);
  const emptyParty = {
    type: 'sellersCreditors',
    title: '',
    name: '',
    firstName: '',
    birthdate: null,
    nationality: '',
    careOf: '',
    street: '',
    addition: '',
    postOffice: '',
    postcode: '',
    location: '',
    phone: '',
    mobile: '',
    email: '',
    bankAccount: ''
  };
  const [party, setParty] = React.useState(emptyParty);
  const [emailErrorText, setEmailErrorText] = React.useState('');

  /**
   * Handle dialog open.
   *
   */
  const handleClickOpen = () => {
    setParty(emptyParty);
    setOpen(true);
  };

  /**
   * Handle dialog close.
   *
   */
  const handleClickClose = () => {
    setOpen(false);
  };

  /**
   * Validates email address.
   *
   * @param {string} email - The entered email.
   * @return {boolean} - The validation result.
   */
  const validateEmailField = (email: string) => {
    return email.match(REGULAR_EXPRESSION_EMAIL);
  };

  /**
   * Handle form change.
   *
   * @param {Event} event - Form change event.
   */
  const handleFormChange = (event: Event) => {
    setParty({
      ...party,
      [event.target.name]: event.target.value
    });
    if (event.target.name === 'email') {
      const emailHelperText = validateEmailField(event.target.value)
        ? ''
        : locale.invalidEmail;
      setEmailErrorText(emailHelperText);
    }
  };

  /**
   * Handle date change.
   *
   * @param {Date} date - Selected date.
   */
  const handleDateChange = (date: Date) => {
    if (date) {
      setParty({
        ...party,
        ['birthdate']: moment(date).format(locale.dateFormat)
      });
    }
  };

  /**
   * Handle delete party.
   *
   * @param {String} partyId - ID of the selected party.
   */
  const handleDeleteParty = (partyId: String) => {
    props.deletePartyHandler(partyId);
  };

  /**
   * Handle form submit.
   *
   * @param {Event} event - Form submit event.
   */
  const handleFormSubmit = (event: Event) => {
    event.preventDefault();
    if (party.id) {
      props.updatePartyHandler(party);
    } else {
      props.addPartyHandler(party);
    }
    setOpen(false);
  };

  /**
   * Handle edit party.
   *
   * @param {Party} party - The selected party.
   */
  const handleEditParty = (party) => {
    setParty(party);
    setOpen(true);
  };

  /**
   * Render method for parties list.
   *
   * @return {String} - The component contents.
   */
  const RenderPartiesList = () => {
    const partiesList = props.parties.map((party, i) => (
      <ListItem
        button
        className={classes.listItem}
        key={i}
        onClick={() => handleEditParty(party)}
      >
        <ListItemAvatar>
          <Avatar className={classes.avatar}>
            <PersonIcon />
          </Avatar>
        </ListItemAvatar>
        <ListItemText primary={party.firstName + ' ' + party.name} />
        <ListItemSecondaryAction>
          <IconButton
            edge='end'
            aria-label='delete'
            disabled={props.disabled}
            color='default'
            onClick={() => {
              handleDeleteParty(party.id);
            }}
          >
            <HighlightOffIcon />
          </IconButton>
        </ListItemSecondaryAction>
      </ListItem>
    ));

    return (
      <div>
        <List className={classes.listBody}>{partiesList}</List>
      </div>
    );
  };

  /**
   * Sub-render method for the select content of the dialog.
   *
   * @return {String} - The component contents.
   */
  const renderDialogContentSelects = () => {
    return (
      <div>
        <FormControl required className={classes.formControlStyle}>
          <InputLabel
            id='document-party-required'
            className={classes.inputLabelStyle}
          >
            {locale.documentParty}
          </InputLabel>
          <Select
            disabled={props.disabled}
            native
            labelId='document-party-required'
            id='document-party'
            value={party.type}
            onChange={handleFormChange}
            name='type'
            className={classes.selectStyle}
          >
            <option value='sellersCreditors'>
              {locale.documentPartySellersCreditors}
            </option>
            <option value='purchaserDebtor'>
              {locale.documentPartyPurchaserDebtor}
            </option>
            <option value='other'>{locale.documentPartyOther}</option>
          </Select>
        </FormControl>
        <FormControl className={classes.formControlStyle}>
          <InputLabel htmlFor='title' className={classes.inputLabelStyle}>
            {locale.title}
          </InputLabel>
          <Select
            disabled={props.disabled}
            value={party.title}
            native
            onChange={handleFormChange}
            name='title'
            className={classes.selectStyle}
            inputProps={{
              id: 'title'
            }}
          >
            <option value='' />
            <option value='ms'>{locale.titleMs}</option>
            <option value='mr'>{locale.titleMr}</option>
            <option value='company'>{locale.titleCompany}</option>
          </Select>
        </FormControl>
      </div>
    );
  };

  /**
   * Sub-render method for the personal content of the dialog.
   *
   * @return {String} - The component contents.
   */
  // eslint-disable-next-line max-lines-per-function
  const renderDialogContentPersonal = () => {
    return (
      <div>
        <TextField
          disabled={props.disabled}
          value={party.name}
          required
          margin='dense'
          name='name'
          label={locale.name}
          type='text'
          fullWidth
          onChange={handleFormChange}
        />
        {(party.title === 'ms' || party.title === 'mr') && (
          <React.Fragment>
            <TextField
              disabled={props.disabled}
              value={party.firstName}
              required
              margin='dense'
              name='firstName'
              label={locale.firstName}
              type='text'
              fullWidth
              onChange={handleFormChange}
            />
            <MuiPickersUtilsProvider utils={DateFnsUtils}>
              <KeyboardDatePicker
                disabled={props.disabled}
                disableToolbar
                autoOk={true}
                variant='inline'
                format={locale.datePickerFormat}
                margin='normal'
                name='birthdate'
                value={party.birthdate}
                label={locale.birthdate}
                invalidDateMessage={locale.datePickerErrorText}
                onChange={handleDateChange}
                fullWidth
              />
            </MuiPickersUtilsProvider>
            <TextField
              disabled={props.disabled}
              value={party.nationality}
              margin='dense'
              name='nationality'
              label={locale.nationality}
              type='text'
              fullWidth
              onChange={handleFormChange}
            />
          </React.Fragment>
        )}
      </div>
    );
  };

  /**
   * Sub-render method for the address content of the dialog.
   *
   * @return {String} - The component contents.
   */
  // eslint-disable-next-line max-lines-per-function
  const renderDialogContentAddress = () => {
    return (
      <div>
        <TextField
          disabled={props.disabled}
          value={party.careOf}
          margin='dense'
          name='careOf'
          label={locale.careOf}
          type='text'
          fullWidth
          onChange={handleFormChange}
        />
        <TextField
          disabled={props.disabled}
          value={party.street}
          margin='dense'
          name='street'
          label={locale.streetAndNum}
          type='text'
          fullWidth
          onChange={handleFormChange}
        />
        <TextField
          disabled={props.disabled}
          value={party.addition}
          margin='dense'
          name='addition'
          label={locale.addition}
          type='text'
          fullWidth
          onChange={handleFormChange}
        />
        <TextField
          disabled={props.disabled}
          value={party.postOffice}
          margin='dense'
          name='postOffice'
          label={locale.postOffice}
          type='text'
          fullWidth
          onChange={handleFormChange}
        />
        <TextField
          disabled={props.disabled}
          value={party.postcode}
          margin='dense'
          name='postcode'
          label={locale.postcode}
          type='text'
          fullWidth
          onChange={handleFormChange}
        />
        <TextField
          disabled={props.disabled}
          value={party.location}
          margin='dense'
          name='location'
          label={locale.location}
          type='text'
          fullWidth
          onChange={handleFormChange}
        />
      </div>
    );
  };

  /**
   * Sub-render method for the contact content of the dialog.
   *
   * @return {String} - The component contents.
   */
  const renderDialogContentContact = () => {
    return (
      <div>
        <TextField
          disabled={props.disabled}
          value={party.phone}
          margin='dense'
          name='phone'
          label={locale.phone}
          type='text'
          fullWidth
          onChange={handleFormChange}
        />
        <TextField
          disabled={props.disabled}
          value={party.mobile}
          margin='dense'
          name='mobile'
          label={locale.mobile}
          type='text'
          fullWidth
          onChange={handleFormChange}
        />
        <TextField
          disabled={props.disabled}
          error={emailErrorText ? true : false}
          helperText={emailErrorText}
          value={party.email}
          margin='dense'
          name='email'
          label={locale.email}
          type='email'
          fullWidth
          onChange={handleFormChange}
        />
        <TextField
          disabled={props.disabled}
          value={party.bankAccount}
          margin='dense'
          name='bankAccount'
          label={locale.bankAccount}
          type='text'
          fullWidth
          onChange={handleFormChange}
        />
      </div>
    );
  };

  /**
   * Sub-render method for the dialog.
   *
   * @return {String} - The component contents.
   */
  const renderDialog = () => {
    return (
      <Dialog
        disableBackdropClick
        open={open}
        onClose={handleClickClose}
        aria-labelledby='form-dialog-title'
        maxWidth={'xs'}
      >
        <DialogTitle id='form-dialog-title'>
          {party.name
            ? locale.documentPartyDialogTitleEdit
            : locale.documentPartyDialogTitle}
          <IconButton
            edge='start'
            color='inherit'
            className={classes.closeIcon}
            onClick={handleClickClose}
            aria-label='close'
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <form onSubmit={handleFormSubmit}>
          <DialogContent style={{paddingTop: 0}}>
            {renderDialogContentSelects()}
            {renderDialogContentPersonal()}
            {renderDialogContentAddress()}
            {renderDialogContentContact()}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleClickClose}
              color='primary'
              variant='outlined'
            >
              {locale.cancelButton}
            </Button>
            {!props.disabled && (
              <Button
                disabled={
                  !party.name ||
                  ((party.title === 'mr' || party.title === 'ms') &&
                    !party.firstName) ||
                  !party.type
                }
                type='submit'
                color='primary'
                variant='contained'
              >
                {party.id ? locale.saveButton : locale.addButton}
              </Button>
            )}
          </DialogActions>
        </form>
      </Dialog>
    );
  };

  return (
    <div>
      <Paper className={classes.paper}>
        <List className={classes.listHead}>
          <ListItem className={classes.listItem}>
            <ListItemText primary={locale.documentParties} />
            <ListItemSecondaryAction>
              {!props.disabled && (
                <IconButton
                  edge='end'
                  aria-label='add'
                  color='primary'
                  onClick={handleClickOpen}
                >
                  <AddIcon />
                </IconButton>
              )}
            </ListItemSecondaryAction>
          </ListItem>
        </List>
        <RenderPartiesList />
      </Paper>
      {renderDialog()}
    </div>
  );
}

DocumentParties.propTypes = {
  disabled: PropTypes.bool,
  addPartyHandler: PropTypes.func.isRequired,
  deletePartyHandler: PropTypes.func.isRequired,
  updatePartyHandler: PropTypes.func.isRequired,
  parties: PropTypes.array.isRequired
};
